import { useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2, FileText, ChevronLeft, ChevronRight } from 'lucide-react';
import { api } from '../lib/api';
import { Navbar } from '../components/Navbar';
import { SiteFooter } from '../components/SiteFooter';

const PER_PAGE = 9;

type PostCard = {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  featuredImageUrl: string | null;
  featuredImageAlt: string;
  category: string;
  publishedAt: string | null;
};

const fmtDate = (d: string | null) =>
  d ? new Date(d).toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' }) : '';

export function Blog() {
  const { data, isLoading } = useQuery({
    queryKey: ['blog', 'index'],
    queryFn: () => api<{ posts: PostCard[] }>('/api/blog'),
  });
  const posts = useMemo(() => data?.posts ?? [], [data]);

  const [params, setParams] = useSearchParams();
  const totalPages = Math.max(1, Math.ceil(posts.length / PER_PAGE));
  // Clamp the requested page to a valid range.
  const page = Math.min(totalPages, Math.max(1, Number(params.get('page')) || 1));
  const pageItems = useMemo(
    () => posts.slice((page - 1) * PER_PAGE, page * PER_PAGE),
    [posts, page],
  );

  function goToPage(p: number) {
    setParams(p === 1 ? {} : { page: String(p) }, { replace: false });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  useEffect(() => {
    document.title = 'Blog de ZettaPost — Redes sociales, contenido y productividad';
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />

      {/* hero */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-14 text-center">
          <h1 className="text-4xl font-extrabold text-slate-900 tracking-tight">Blog de ZettaPost</h1>
          <p className="mt-3 text-slate-500 max-w-xl mx-auto">
            Guías, tutoriales y comparativas sobre redes sociales, programación de contenido y
            productividad para marcas, agencias y creadores.
          </p>
        </div>
      </header>

      <main className="flex-1 max-w-6xl mx-auto px-6 py-12 w-full">
        {isLoading ? (
          <div className="py-20 flex items-center justify-center gap-2 text-slate-400">
            <Loader2 size={18} className="animate-spin" /> Cargando artículos…
          </div>
        ) : posts.length === 0 ? (
          <div className="py-20 flex flex-col items-center justify-center gap-3 text-slate-400">
            <FileText size={30} />
            <p>Pronto publicaremos los primeros artículos.</p>
          </div>
        ) : (
          <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {pageItems.map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                className="group bg-white rounded-2xl border border-slate-200 overflow-hidden flex flex-col hover:border-brand-300 hover:shadow-md transition"
              >
                <div className="aspect-[16/9] bg-slate-100 overflow-hidden">
                  {post.featuredImageUrl && (
                    <img
                      src={post.featuredImageUrl}
                      alt={post.featuredImageAlt}
                      className="h-full w-full object-cover group-hover:scale-[1.03] transition-transform duration-300"
                      loading="lazy"
                    />
                  )}
                </div>
                <div className="p-5 flex flex-col flex-1">
                  {post.category && (
                    <span className="text-xs font-semibold text-brand-600 uppercase tracking-wide">
                      {post.category}
                    </span>
                  )}
                  <h2 className="mt-1.5 font-bold text-slate-900 leading-snug group-hover:text-brand-700 transition">
                    {post.title}
                  </h2>
                  <p className="mt-2 text-sm text-slate-500 line-clamp-3 flex-1">{post.excerpt}</p>
                  <p className="mt-3 text-xs text-slate-400">{fmtDate(post.publishedAt)}</p>
                </div>
              </Link>
            ))}
          </div>

          {/* Pagination — 9 posts per page. */}
          {totalPages > 1 && (
            <nav className="mt-10 flex flex-wrap items-center justify-center gap-1.5" aria-label="Paginación del blog">
              <button
                onClick={() => goToPage(page - 1)}
                disabled={page === 1}
                className="h-9 w-9 inline-flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-white hover:border-slate-300 disabled:opacity-40 disabled:cursor-not-allowed transition"
                aria-label="Página anterior"
              >
                <ChevronLeft size={16} />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => goToPage(p)}
                  aria-current={p === page ? 'page' : undefined}
                  className={
                    p === page
                      ? 'h-9 min-w-9 px-3 inline-flex items-center justify-center rounded-lg bg-brand-600 text-white text-sm font-semibold'
                      : 'h-9 min-w-9 px-3 inline-flex items-center justify-center rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-white hover:border-slate-300 transition'
                  }
                >
                  {p}
                </button>
              ))}
              <button
                onClick={() => goToPage(page + 1)}
                disabled={page === totalPages}
                className="h-9 w-9 inline-flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-white hover:border-slate-300 disabled:opacity-40 disabled:cursor-not-allowed transition"
                aria-label="Página siguiente"
              >
                <ChevronRight size={16} />
              </button>
            </nav>
          )}

          {/* Every article as a plain link. The pagination above is made of
              buttons, which crawlers don't follow, so without this list any
              article past page 1 was linked from nowhere on the site. Folded
              away for readers; the links are in the DOM either way. */}
          <details className="mt-14 bg-white rounded-2xl border border-slate-200 group">
            <summary className="cursor-pointer select-none px-6 py-4 font-semibold text-slate-900 list-none flex items-center justify-between">
              Todos los artículos ({posts.length})
              <ChevronRight size={16} className="text-slate-400 transition group-open:rotate-90" />
            </summary>
            <ul className="px-6 pb-6 sm:columns-2 gap-8 text-sm">
              {posts.map((post) => (
                <li key={post.id} className="py-1.5 break-inside-avoid">
                  <Link to={`/blog/${post.slug}`} className="text-slate-700 hover:text-brand-700">
                    {post.title}
                  </Link>
                </li>
              ))}
            </ul>
          </details>
          </>
        )}
      </main>

      <SiteFooter />
    </div>
  );
}
